import { createContext, useContext, useEffect, useState } from "react";
import data from "../data/regions.json";
import { api } from "./api";

export const REGIONS = data.regions;
export const SERVICE_SEO = data.service_seo;

export const ALL_PLACES = REGIONS.flatMap((r) =>
  r.places.map((p) => ({ ...p, regionSlug: r.slug, regionName: r.name, area: r.area }))
);

export const findPlace = (slug) => ALL_PLACES.find((p) => p.slug === slug);
export const placesOfRegion = (regionSlug) => ALL_PLACES.filter((p) => p.regionSlug === regionSlug);
export const neighbours = (place, limit = 6) =>
  placesOfRegion(place.regionSlug).filter((p) => p.slug !== place.slug).slice(0, limit);

const SITE = "NENOX";

let gbpUrl = "";

const SeoConfigContext = createContext({});

export const SeoProvider = ({ children }) => {
  const [cfg, setCfg] = useState({});
  useEffect(() => {
    api.get("/seo").then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, []);
  gbpUrl = cfg.site?.gbp_url || "";
  useEffect(() => {
    const code = cfg.site?.google_verification;
    if (code)
      upsert('meta[name="google-site-verification"]', () => document.createElement("meta"), {
        name: "google-site-verification",
        content: code,
      });
  }, [cfg]);
  return <SeoConfigContext.Provider value={cfg}>{children}</SeoConfigContext.Provider>;
};

function upsert(selector, create, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

export const useSeoConfig = () => useContext(SeoConfigContext);

const withBrand = (t) => (!t ? SITE : t.includes(SITE) ? t : `${t} | ${SITE}`);

export function useSeo({ configKey, title, description, canonical, jsonLd, noindex }) {
  const cfg = useContext(SeoConfigContext);
  const override = (configKey && cfg[configKey]) || {};
  const finalTitle = override.title || title;
  const finalDesc = override.description || description;

  useEffect(() => {
    if (finalTitle) document.title = withBrand(finalTitle);
    if (finalDesc)
      upsert('meta[name="description"]', () => document.createElement("meta"), {
        name: "description",
        content: finalDesc,
      });
    const base = process.env.REACT_APP_SITE_URL || window.location.origin;
    const url = `${base.replace(/\/$/, "")}${canonical || window.location.pathname}`;
    const existingCanonical = document.head.querySelector('link[rel="canonical"]');
    if (noindex) {
      if (existingCanonical) existingCanonical.remove();
    } else {
      upsert('link[rel="canonical"]', () => document.createElement("link"), { rel: "canonical", href: url });
    }
    const isPreview =
      process.env.REACT_APP_SITE_URL && !window.location.origin.startsWith(process.env.REACT_APP_SITE_URL);
    upsert('meta[name="robots"]', () => document.createElement("meta"), {
      name: "robots",
      content: noindex || isPreview ? "noindex, nofollow" : "index, follow",
    });
    upsert('meta[property="og:title"]', () => document.createElement("meta"), {
      property: "og:title",
      content: withBrand(finalTitle),
    });
    if (finalDesc)
      upsert('meta[property="og:description"]', () => document.createElement("meta"), {
        property: "og:description",
        content: finalDesc,
      });
    upsert('meta[property="og:url"]', () => document.createElement("meta"), { property: "og:url", content: url });
    const siteBase = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, "");
    const ogImage = `${siteBase}/og-image.jpg`;
    [
      ["og:type", "website"],
      ["og:site_name", SITE],
      ["og:locale", "de_DE"],
      ["og:image", ogImage],
      ["og:image:secure_url", ogImage],
      ["og:image:width", "1200"],
      ["og:image:height", "630"],
      ["og:image:type", "image/jpeg"],
      ["og:image:alt", "NENOX – Hausmeisterservice, Reinigung, Entrümpelung und Umzüge in Regensburg"],
    ].forEach(([property, content]) =>
      upsert(`meta[property="${property}"]`, () => document.createElement("meta"), { property, content })
    );
    [
      ["twitter:card", "summary_large_image"],
      ["twitter:title", withBrand(finalTitle)],
      ["twitter:description", finalDesc || ""],
      ["twitter:image", ogImage],
    ].forEach(([name, content]) =>
      upsert(`meta[name="${name}"]`, () => document.createElement("meta"), { name, content })
    );

    const old = document.getElementById("nx-jsonld");
    if (old) old.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.id = "nx-jsonld";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalTitle, finalDesc, canonical, noindex, JSON.stringify(jsonLd || null)]);
}

export const localBusinessLd = (extra = {}) => ({
  "@context": "https://schema.org",
  "@type": "HomeAndConstructionBusiness",
  name: "NENOX",
  url: "https://nenox-dienstleistungen.de",
  telephone: ["+4916099007777", "+491716171747"],
  email: "nenox@mail.de",
  vatID: "DE358121695",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Weiherweg 14",
    postalCode: "93051",
    addressLocality: "Regensburg",
    addressRegion: "Bayern",
    addressCountry: "DE",
  },
  priceRange: "€€",
  geo: { "@type": "GeoCoordinates", latitude: 49.0134, longitude: 12.1016 },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
    },
  ],
  ...(gbpUrl ? { sameAs: [gbpUrl], hasMap: gbpUrl } : {}),
  ...extra,
});
